import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import SearchPicker, { PickerOption } from './SearchPicker';
import pettyCashApi from '../../services/pettyCashApi';
import type { AuxiliaryList, CodeName, Dims, DimensionInfo, GlPreview, GlPreviewRow, GlPreviewSide, PettyCashCategory } from '../../types/pettyCash';

/**
 * An expense category. Its G/L account comes from SAP's Advanced G/L rules, as for a purchase through
 * PR -> PO -> receipt: for project claims and for department claims it names an SAP item (the item's own
 * rules and class apply) or an item class. A project claim follows its project's warehouse, a department
 * claim the department warehouse of the Settings. The fixed account is used when the rules give none.
 * Each side also names the auxiliary (SAP Dimension 5) its receipts carry. Also: the other dimension codes its
 * lines carry by default, and whether its VAT can be claimed.
 */

export type CategoryInput = Omit<PettyCashCategory, 'categoryId' | 'projectAccountName' | 'costCenterAccountName' | 'projectItemName' | 'deptItemName' | 'projectAuxiliaryName' | 'deptAuxiliaryName'> & { categoryId?: number };

/** The auxiliary dimension: each side names its own, so it is not among the other dimension codes. */
const AUX_DIMENSION = 5;

interface Props {
  open: boolean;
  companyId: number;
  category: PettyCashCategory | null;
  dimensions: DimensionInfo[];
  /** SAP's item classes (U_Class); empty when SAP lists none (the class is then typed). */
  itemClasses: CodeName[];
  nextSortOrder: number;
  onClose: () => void;
  onSave: (input: CategoryInput) => Promise<void>;
}

function DimCodePicker({ companyId, dim, value, onChange }: { companyId: number; dim: DimensionInfo; value: string; onChange: (v: string) => void }) {
  const [options, setOptions] = useState<CodeName[] | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || options) return;
    pettyCashApi.dimensionCodes(companyId, dim.code).then(setOptions).catch(() => setOptions([]));
  }, [open, options, companyId, dim.code]);
  const current = value ? (options?.find((o) => o.code === value) || { code: value, name: '' }) : null;
  return (
    <Autocomplete
      id={`pc-cat-dim-${dim.code}`}
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={current}
      options={options || (current ? [current] : [])}
      loading={open && !options}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
      onChange={(_e, v) => onChange(v?.code || '')}
      renderInput={(params) => <TextField {...params} label={dim.label} helperText="Empty: none" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
    />
  );
}

/**
 * One side's default auxiliary (SAP Dimension 5), offered first on its receipts, where the holder chooses from the
 * codes the receipt's account takes: the codes of the type its fixed account takes, or the type SAP's rules give that
 * side. A side whose account takes no auxiliary has none to offer. Searchable; at most 200 shown at a time.
 */
function AuxPicker({ id, label, companyId, account, auxType, value, onChange }: {
  id: string;
  label: string;
  companyId: number;
  account: string;
  auxType: string | null;
  value: CodeName | null;
  onChange: (v: CodeName | null) => void;
}) {
  const [list, setList] = useState<AuxiliaryList | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setList(null);
    setFailed(false);
    pettyCashApi.auxiliaries(companyId, account ? { account } : auxType ? { type: auxType } : {})
      .then((l) => { if (!cancelled) setList(l); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [companyId, account, auxType]);
  const options: CodeName[] = (list?.codes || []).map((c) => ({ code: c.code, name: c.name }));
  const typeName = list?.typeName || list?.type || auxType || '';
  // No auxiliary type for this side (its account takes none, or there is no account yet): nothing to offer.
  const none = !!list && !list.type && !list.account?.auxMandatory;
  const help = failed
    ? 'The auxiliaries could not be read from SAP'
    : !list
      ? 'Reading the auxiliaries…'
      : none
        ? `${account ? `${account} takes` : 'No account yet, so'} no auxiliary${value ? ': clear it' : ''}`
        : `Offered first on its receipts; the holder can choose any ${typeName} code`;
  return (
    <Autocomplete
      id={id}
      size="small"
      value={value}
      disabled={none && !value}
      options={value && !options.some((o) => o.code === value.code) ? [value, ...options] : options}
      loading={!list && !failed}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
      filterOptions={(opts, state) => {
        const words = state.inputValue.toLowerCase().split(/\s+/).filter(Boolean);
        return opts.filter((o) => words.every((w) => `${o.code} ${o.name}`.toLowerCase().includes(w))).slice(0, 200);
      }}
      onChange={(_e, v) => onChange(v)}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={help} error={failed} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
      )}
    />
  );
}

/** The one auxiliary type SAP's rules give a side's warehouses (null when none, or when they differ). */
function sideAuxType(side: GlPreviewSide | null | undefined): string | null {
  const types = Array.from(new Set((side?.rows || []).map((r) => r.auxType).filter((t): t is string => !!t)));
  return types.length === 1 ? types[0] : null;
}

/** One warehouse type (project) or the department warehouse: the account SAP's rules give, and the rule. */
function PreviewRow({ row }: { row: GlPreviewRow }) {
  const account = row.account || (!row.rule ? row.fixedAccount : null);
  const accountText = account
    ? `${account}${row.account && row.accountName ? ` ${row.accountName}` : ''}${!row.rule ? ' (fixed account)' : ''}`
    : 'No account: finance chooses one on each receipt';
  const aux = row.auxType ? ` · ${row.auxMandatory ? 'needs' : 'takes'} a ${row.auxType} auxiliary (Dimension 5)` : '';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25, alignItems: 'baseline', py: 0.75, borderTop: '1px solid #eceff1' }}>
      <Typography variant="body2" fontWeight={600} sx={{ flex: '0 0 170px', minWidth: 0 }}>
        {row.label}
        {row.warehouseType && row.warehouseCount > 1 ? <Typography component="span" variant="caption" color="text.secondary"> · {row.warehouseCount} warehouses</Typography> : null}
      </Typography>
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color={account ? 'text.primary' : 'warning.main'}>{accountText}</Typography>
          {row.rule && account ? (
            <Chip size="small" variant="outlined" color={row.issued ? 'success' : 'default'} label={row.issued ? 'Cost' : 'Stays on the clearing account'} />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {row.rule ? `SAP rule ${row.rule.code}${row.rule.description ? ` ${row.rule.description}` : ''}` : 'No SAP rule gives an account'}{aux}
        </Typography>
      </Box>
    </Box>
  );
}

function PreviewSide({ side, emptyText }: { side: GlPreviewSide | null | undefined; emptyText: string }) {
  if (!side) return <Typography variant="caption" color="text.secondary">{emptyText}</Typography>;
  return (
    <Box>
      {side.itemMissing ? (
        <Alert severity="error" sx={{ mb: 1 }}>SAP has no item {side.item?.code}.</Alert>
      ) : null}
      {side.rows.length ? side.rows.map((r) => <PreviewRow key={`${r.warehouseCode || ''}|${r.warehouseType || ''}|${r.label}`} row={r} />) : (
        <Typography variant="caption" color="text.secondary">No warehouse to read the rules for.</Typography>
      )}
    </Box>
  );
}

const CategoryDialog: React.FC<Props> = ({ open, companyId, category, dimensions, itemClasses, nextSortOrder, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [projectItem, setProjectItem] = useState('');
  const [projectClass, setProjectClass] = useState('');
  const [deptItem, setDeptItem] = useState('');
  const [deptClass, setDeptClass] = useState('');
  const [projectAccount, setProjectAccount] = useState<PickerOption | null>(null);
  const [costCenterAccount, setCostCenterAccount] = useState<PickerOption | null>(null);
  const [projectAux, setProjectAux] = useState<CodeName | null>(null);
  const [deptAux, setDeptAux] = useState<CodeName | null>(null);
  const [dims, setDims] = useState<Dims>({});
  const [vatDeductible, setVatDeductible] = useState(true);
  const [active, setActive] = useState(true);
  const [sortText, setSortText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GlPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const ticket = useRef(0);

  useEffect(() => {
    if (!open) return;
    setName(category?.name || '');
    setProjectItem(category?.projectItem || '');
    setProjectClass(category?.projectClass || '');
    setDeptItem(category?.deptItem || '');
    setDeptClass(category?.deptClass || '');
    setProjectAccount(category?.projectAccount ? { code: category.projectAccount, name: category.projectAccountName || '' } : null);
    setCostCenterAccount(category?.costCenterAccount ? { code: category.costCenterAccount, name: category.costCenterAccountName || '' } : null);
    // Each side's auxiliary; an older server sends one default for both in Dimension 5 of the codes.
    const single = category?.dims?.[String(AUX_DIMENSION)] || '';
    setProjectAux(category?.projectAuxiliary ? { code: category.projectAuxiliary, name: category.projectAuxiliaryName || '' } : single ? { code: single, name: '' } : null);
    setDeptAux(category?.deptAuxiliary ? { code: category.deptAuxiliary, name: category.deptAuxiliaryName || '' } : single ? { code: single, name: '' } : null);
    const otherDims: Dims = { ...(category?.dims || {}) };
    delete otherDims[String(AUX_DIMENSION)];
    setDims(otherDims);
    setVatDeductible(category?.vatDeductible ?? true);
    setActive(category?.isActive ?? true);
    setSortText(String(category?.sortOrder ?? nextSortOrder));
    setError(null);
    setBusy(false);
    setPreview(null);
    setPreviewError(null);
  }, [open, category, nextSortOrder]);

  // What SAP's rules give, read again 600 ms after the last change. An item wins over a class.
  const pItem = projectItem.trim();
  const dItem = deptItem.trim();
  const pClass = pItem ? '' : projectClass;
  const dClass = dItem ? '' : deptClass;
  const pAccount = projectAccount?.code || '';
  const dAccount = costCenterAccount?.code || '';
  useEffect(() => {
    if (!open || !companyId) return undefined;
    if (!pItem && !pClass && !dItem && !dClass) {
      ticket.current += 1;
      setPreview(null);
      setPreviewBusy(false);
      setPreviewError(null);
      return undefined;
    }
    const mine = ++ticket.current;
    const timer = setTimeout(() => {
      setPreviewBusy(true);
      setPreviewError(null);
      pettyCashApi.glPreview(companyId, {
        projectItem: pItem || null,
        projectClass: pClass || null,
        deptItem: dItem || null,
        deptClass: dClass || null,
        projectAccount: pAccount || null,
        costCenterAccount: dAccount || null,
      })
        .then((p) => { if (mine === ticket.current) setPreview(p); })
        .catch((e: any) => { if (mine === ticket.current) { setPreview(null); setPreviewError(e?.response?.data?.message || 'SAP\'s G/L rules could not be read'); } })
        .finally(() => { if (mine === ticket.current) setPreviewBusy(false); });
    }, 600);
    return () => clearTimeout(timer);
  }, [open, companyId, pItem, pClass, dItem, dClass, pAccount, dAccount]);

  const search = useCallback(async (q: string): Promise<PickerOption[]> => {
    const rows = await pettyCashApi.searchAccounts(companyId, q);
    return rows.map((r) => ({ code: r.code, name: r.name }));
  }, [companyId]);

  const save = async () => {
    if (!name.trim()) { setError('Give the category a name.'); return; }
    const projectSet = !!(pItem || pClass || projectAccount);
    const deptSet = !!(dItem || dClass || costCenterAccount);
    if (!projectSet && !deptSet) { setError('Give the category an SAP item or class, or a fixed account, for projects or for departments.'); return; }
    const cleaned: Dims = {};
    for (const [k, v] of Object.entries(dims)) if (v && k !== String(AUX_DIMENSION)) cleaned[k] = v;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        categoryId: category?.categoryId,
        name: name.trim().slice(0, 100),
        projectItem: pItem || null,
        projectClass: pClass || null,
        deptItem: dItem || null,
        deptClass: dClass || null,
        projectAccount: projectAccount?.code || null,
        costCenterAccount: costCenterAccount?.code || null,
        projectAuxiliary: projectAux?.code || null,
        deptAuxiliary: deptAux?.code || null,
        dims: cleaned,
        vatDeductible,
        isActive: active,
        sortOrder: Number(sortText) || 0,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The category could not be saved');
    } finally {
      setBusy(false);
    }
  };

  /** The item, class and fixed account of one side (project claims or department claims). */
  const sideFields = (side: 'project' | 'dept') => {
    const project = side === 'project';
    const item = project ? projectItem : deptItem;
    const setItem = project ? setProjectItem : setDeptItem;
    const cls = project ? projectClass : deptClass;
    const setCls = project ? setProjectClass : setDeptClass;
    const previewSide = project ? preview?.project : preview?.department;
    const itemHelp = !item.trim()
      ? 'Optional: an item with its own G/L rules'
      : previewSide?.itemMissing ? `SAP has no item ${item.trim()}`
        : previewSide?.item?.name ? previewSide.item.name : 'Its own G/L rules and class apply';
    const classHelp = item.trim()
      ? `The item's own class${previewSide?.itemClass ? `: ${previewSide.itemClass}` : ''}`
      : 'The rules of this class apply';
    const knownClass = !cls || itemClasses.some((c) => c.code === cls);
    const fixedAccount = project ? projectAccount : costCenterAccount;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            id={`pc-cat-${side}-item`}
            size="small"
            label="SAP item"
            value={item}
            onChange={(e) => setItem(e.target.value.replace(/\s+/g, '').slice(0, 50))}
            error={!!item.trim() && !!previewSide?.itemMissing}
            helperText={itemHelp}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {item.trim() ? (
            // An item brings its own class: shown, not chosen.
            <TextField
              id={`pc-cat-${side}-class`}
              size="small"
              label="Item class"
              value={previewSide?.itemClass || ''}
              placeholder="From the item"
              disabled
              helperText={classHelp}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          ) : itemClasses.length ? (
            <TextField
              id={`pc-cat-${side}-class`}
              select
              size="small"
              label="Item class"
              value={cls}
              onChange={(e) => setCls(e.target.value)}
              helperText={classHelp}
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {!knownClass ? <MenuItem value={cls}>{cls} (not in SAP's list)</MenuItem> : null}
              {itemClasses.map((c) => <MenuItem key={c.code} value={c.code}>{c.name && c.name !== c.code ? `${c.code} · ${c.name}` : c.code}</MenuItem>)}
            </TextField>
          ) : (
            <TextField
              id={`pc-cat-${side}-class`}
              size="small"
              label="Item class"
              value={cls}
              onChange={(e) => setCls(e.target.value.slice(0, 100))}
              helperText="As SAP spells it (U_Class)"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <SearchPicker
            id={`pc-cat-${side}-account`}
            label="Fixed account"
            value={fixedAccount}
            onChange={project ? setProjectAccount : setCostCenterAccount}
            search={search}
            helperText="When SAP's rules give none"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AuxPicker
            id={`pc-cat-${side}-aux`}
            label="Default auxiliary (optional)"
            companyId={companyId}
            account={fixedAccount?.code || ''}
            auxType={fixedAccount ? null : sideAuxType(previewSide)}
            value={project ? projectAux : deptAux}
            onChange={project ? setProjectAux : setDeptAux}
          />
        </Grid>
      </Grid>
    );
  };

  const anyRules = !!(pItem || pClass || dItem || dClass);

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="pc-cat-title">
      <DialogTitle id="pc-cat-title">{category ? `Expense category: ${category.name}` : 'Add an expense category'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField id="pc-cat-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth helperText="As holders will see it, e.g. Vehicle fuel and oil" />

          <Box>
            <Typography variant="subtitle2">G/L account and auxiliary</Typography>
            <Typography variant="body2" color="text.secondary">
              SAP's Advanced G/L rules choose the account, as for a purchase through PR → PO → receipt. Name an SAP item (it has its own rules) or an item class.
              A project claim follows its project's warehouse; a department claim the department warehouse in Settings.
              When the account takes an auxiliary (Dimension 5), the holder chooses it on each receipt from the codes of the account&apos;s type; a
              default here is offered first.
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>Project claims</Typography>
            {sideFields('project')}
            {anyRules ? (
              <PreviewSide side={preview?.project} emptyText={previewBusy ? '' : pItem || pClass ? '' : 'No item or class: the fixed account applies to project claims.'} />
            ) : null}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600}>Department claims</Typography>
            {sideFields('dept')}
            {anyRules ? (
              <PreviewSide side={preview?.department} emptyText={previewBusy ? '' : dItem || dClass ? '' : 'No item or class: the fixed account applies to department claims.'} />
            ) : null}
          </Stack>

          {previewBusy ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Reading SAP's G/L rules…</Typography>
            </Stack>
          ) : null}
          {previewError ? <Alert severity="warning">{previewError}</Alert> : null}
          {preview?.year && anyRules ? <Typography variant="caption" color="text.secondary">SAP's G/L rules of {preview.year}, ranked as SAP ranks them.</Typography> : null}
          {preview?.warnings?.length ? (
            <Alert severity="warning">
              {preview.warnings.map((w) => <div key={w}>{w}</div>)}
            </Alert>
          ) : null}

          {dimensions.some((d) => d.code !== AUX_DIMENSION) ? (
            <>
              <Divider />
              <Typography variant="subtitle2">Other dimension codes of its receipts (used when the claim leaves them empty)</Typography>
              <Grid container spacing={2}>
                {dimensions.filter((d) => d.code !== AUX_DIMENSION).map((d) => (
                  <Grid item xs={12} sm={6} md={4} key={d.code}>
                    <DimCodePicker companyId={companyId} dim={d} value={dims[String(d.code)] || ''} onChange={(v) => setDims((x) => ({ ...x, [String(d.code)]: v }))} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : null}
          <FormControlLabel control={<Checkbox checked={vatDeductible} onChange={(e) => setVatDeductible(e.target.checked)} />} label="Its VAT can be claimed (untick for expenses whose input VAT is blocked, e.g. entertainment)" />
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active" />
            <TextField id="pc-cat-sort" label="Order in lists" value={sortText} onChange={(e) => setSortText(e.target.value.replace(/[^\d]/g, ''))} inputProps={{ inputMode: 'numeric' }} sx={{ width: 140 }} />
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default CategoryDialog;
